import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  initVersionedSchema,
  type ISnippet,
  type MixedSchema,
  TextFormatting,
  toTextContent,
} from '@principle-theorem/editor';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import {
  ISnippetFormData,
  SnippetFormComponent,
} from '@principle-theorem/ng-principle';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets, NgCommonModule } from '@principle-theorem/ng-shared';
import { type IStaffer } from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  resolveProp$,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { noop, type Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-snippet-detail',
  templateUrl: './snippet-detail.component.html',
  styleUrls: ['./snippet-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    ContentViewComponent,
  ],
})
export class SnippetDetailComponent {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _organisation = inject(OrganisationService);
  private _destroyRef = inject(DestroyRef);

  snippet$: Observable<WithRef<ISnippet>>;

  constructor() {
    this.snippet$ = resolveProp$<WithRef<ISnippet>>(
      this._route.data,
      'snippet'
    );
  }

  getSnippetKeyword(snippet: ISnippet): MixedSchema {
    return initVersionedSchema([
      toTextContent('Keyword: '),
      toTextContent(snippet.keyword ?? '', {}, [{ type: TextFormatting.Code }]),
    ]);
  }

  async openSnippetForm(snippet: WithRef<ISnippet>): Promise<void> {
    const staffer: WithRef<IStaffer> = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    const data: ISnippetFormData = {
      staffer,
      snippet,
    };

    const updatedSnippet = await this._dialog
      .open<SnippetFormComponent, ISnippetFormData, ISnippet>(
        SnippetFormComponent,
        DialogPresets.large({
          data,
        })
      )
      .afterClosed()
      .toPromise();

    if (!updatedSnippet) {
      return;
    }

    await Firestore.patchDoc(snippet.ref, updatedSnippet);
    this._snackBar.open('Snippet Updated');
  }

  async deleteSnippet(snippet: WithRef<ISnippet>): Promise<void> {
    if (snippet.deleted) {
      return;
    }
    await Firestore.deleteDoc(snippet.ref);

    this._snackBar
      .open('Snippet Deleted', 'UNDO')
      .onAction()
      .pipe(
        switchMap(() => Firestore.patchDoc(snippet.ref, { deleted: false })),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(noop);

    await this._router.navigate(['../'], { relativeTo: this._route });
  }

  async undoDelete(snippet: WithRef<ISnippet>): Promise<void> {
    if (!snippet.deleted) {
      return;
    }
    await Firestore.patchDoc(snippet.ref, {
      deleted: false,
    });
    this._snackBar.open('Snippet Restored');
  }
}
